"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import type { CampaignPageSection } from "@growwithai/shared";
import SafeImage from "@/components/common/SafeImage";
import { isSafeHref } from "../types";
import { sectionBgStyle, sectionHeadingStyle } from "../section-style";
type HeroProps = Extract<CampaignPageSection, {
  type: "hero";
}>;

/** Section pembuka: heading besar, subheading, CTA, dan gambar opsional. */
export default function HeroSection(t0) {
  const $ = _c(39);
  const {
    section,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "homepage" : t1;
  const hasImage = !!section.image_url;
  let t2;
  if ($[0] !== section.cta) {
    t2 = section.cta && isSafeHref(section.cta.href);
    $[0] = section.cta;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const ctaOk = t2;
  const hasCustomBg = !!section.custom_style?.background_color;
  let t3;
  if ($[2] !== section.custom_style) {
    t3 = sectionBgStyle(section.custom_style);
    $[2] = section.custom_style;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== hasCustomBg) {
    t4 = !hasCustomBg && <><div aria-hidden={true} className="pointer-events-none absolute inset-0" style={{
        background: "linear-gradient(160deg, color-mix(in srgb, var(--brand-primary) 10%, white) 0%, white 55%, color-mix(in srgb, var(--brand-accent) 7%, white) 100%)"
      }} /><div aria-hidden={true} className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full opacity-20 blur-3xl" style={{
        background: "var(--brand-primary)"
      }} /></>;
    $[4] = hasCustomBg;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `relative mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:py-24 ${hasImage ? "md:grid-cols-2" : "text-center"}`;
  const t6 = hasImage ? "" : "mx-auto max-w-3xl";
  let t7;
  if ($[6] !== section.custom_style) {
    t7 = sectionHeadingStyle(section.custom_style);
    $[6] = section.custom_style;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== section.heading || $[9] !== t7) {
    t8 = <h1 className="text-4xl font-extrabold leading-[1.12] tracking-tight text-warm-black md:text-5xl" style={t7}>{section.heading}</h1>;
    $[8] = section.heading;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== section.subheading) {
    t9 = section.subheading && <p className="mt-4 text-base leading-relaxed text-gray-600 md:text-lg">{section.subheading}</p>;
    $[11] = section.subheading;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = `mt-8 flex flex-wrap items-center gap-3 ${hasImage ? "" : "justify-center"}`;
  let t11;
  if ($[13] !== ctaOk || $[14] !== section.cta) {
    t11 = ctaOk && section.cta && <Link href={section.cta.href} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[var(--brand-primary)] px-6 text-[15px] font-semibold text-[var(--brand-on-primary,#fff)] shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.14)] transition-colors hover:bg-[var(--brand-primary-hover)]">{section.cta.label}<ArrowRight className="h-4 w-4" /></Link>;
    $[13] = ctaOk;
    $[14] = section.cta;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== variant) {
    t12 = variant === "homepage" && <Link href="/register" className="inline-flex h-12 items-center justify-center rounded-xl border border-black/10 bg-white px-6 text-[15px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50">Daftar Gratis</Link>;
    $[16] = variant;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12) {
    t13 = <div className={t10}>{t11}{t12}</div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t6 || $[24] !== t8 || $[25] !== t9) {
    t14 = <div className={t6}>{t8}{t9}{t13}</div>;
    $[22] = t13;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== hasImage || $[28] !== section.heading || $[29] !== section.image_url) {
    t15 = hasImage && <div className="relative"><SafeImage src={section.image_url as string} alt={section.heading} loading="eager" fetchPriority="high" className="aspect-[16/10] w-full rounded-3xl border border-black/[0.06] object-cover shadow-xl" /></div>;
    $[27] = hasImage;
    $[28] = section.heading;
    $[29] = section.image_url;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15 || $[33] !== t5) {
    t16 = <div className={t5}>{t14}{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t5;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16 || $[36] !== t3 || $[37] !== t4) {
    t17 = <section className="relative overflow-hidden bg-white" style={t3}>{t4}{t16}</section>;
    $[35] = t16;
    $[36] = t3;
    $[37] = t4;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}